import { clsx, type ClassValue } from 'clsx'
import { extendTailwindMerge } from 'tailwind-merge'
import { defaultConfig } from 'tailwind-variants'

const colors = [
  'branding-white-50',
  'branding-white-100',
  'branding-black-50',
  'branding-black-100',
  'surface-white-50',
  'surface-white-100',
  'surface-white-200',
  'surface-white-300',
  'surface-white-400',
  'surface-black-50',
  'surface-black-100',
  'surface-black-200',
  'primary-50',
  'primary-100',
  'primary-200',
  'primary-300',
  'primary-400',
  'primary-500',
  'secondary-50',
  'secondary-100',
  'secondary-200',
  'secondary-300',
  'secondary-400',
  'secondary-500',
  'danger-50',
  'danger-100',
  'danger-200',
  'danger-300',
  'danger-400',
  'success-50',
  'success-100',
  'success-200',
  'success-300',
  'success-400',
  'warning-50',
  'warning-100',
  'warning-200',
  'warning-300',
  'warning-400',
  'information-50',
  'information-100',
  'information-200',
  'information-300',
  'information-400',
  'opacity-modal-bg',
  'generic-ethereal-ivory',
  'generic-sophisticated-sage',
  'generic-timeless-noir',
  'generic-muted-moss',
  'generic-cerulean-blue',
  'generic-grass-green',
  'generic-pacific-blue',
  'generic-yarrow-gold',
  'generic-soft-mint-green',
  'generic-deep-teal',
  'generic-light-sky-blue',
  'generic-light-gray',
  'generic-bright-orange',
  'generic-light-orange',
  'generic-muted-pink',
  'generic-pale-peach',
  'generic-dark-blue',
  'generic-medium-blue',
  'generic-light-blue',
  'generic-pale-blue',
  'generic-forest-green',
  'generic-bright-green',
  'generic-lime-green',
  'generic-warm-yellow',
  'generic-dark-green-gray',
  'generic-light-gray-green',
  'generic-soft-gray',
  'generic-off-white',
  'generic-antique-white',
  'generic-terra-cotta',
  'generic-deep-slate-blue',
  'generic-muted-sage-green',
  'generic-pastel-pink',
  'generic-pastel-peach',
  'generic-pastel-yellow',
  'generic-pastel-green',
  'generic-dark-indigo',
  'generic-deep-purple-gray',
  'generic-cool-gray',
  'generic-light-gray-2',
  'generic-dark-terra-cotta',
  'generic-orange-white',
  'generic-blue-white',
  'organization-frame-bg',
  'organization-screens-bg',
  'organization-title-bg',
  'organization-text-color',
  'organization-components-bg',
  'organization-components-stroke',
  'links-fresh-link',
  'links-clicked-link',
  'surface-50',
  'surface-200',
  'surface-300',
  'surface-disabled-bg',
  'surface-disabled-text',
  'surface-title-50',
  'surface-title-100',
  'surface-title-200',
  'surface-body',
  'on-surface',
  'on-surface-secondary',
  'on-surface-title',
  'primary-75',
  'primary-350'
]

const fontSizes = ['font-heading', 'font-body']

const textSizes = [
  /* we need to add all the text sizes available in the theme
   * to the text class group, so that the twMerge can merge the text sizes
   * correctly
   */
  /* heading sizes */
  'heading-h1',
  'heading-h1-xs',
  'heading-h1-sm',
  'heading-h1-md',
  'heading-h1-lg',
  'heading-h1-xl',
  'heading-h1-2xl',
  'heading-h1-3xl',
  'heading-h1-4xl',
  'heading-h2',
  'heading-h2-xs',
  'heading-h2-sm',
  'heading-h2-md',
  'heading-h2-lg',
  'heading-h2-xl',
  'heading-h2-2xl',
  'heading-h2-3xl',
  'heading-h2-4xl',
  'heading-h3',
  'heading-h3-xs',
  'heading-h3-sm',
  'heading-h3-md',
  'heading-h3-lg',
  'heading-h3-xl',
  'heading-h3-2xl',
  'heading-h3-3xl',
  'heading-h3-4xl',
  'heading-h4',
  'heading-h4-xs',
  'heading-h4-sm',
  'heading-h4-md',
  'heading-h4-lg',
  'heading-h4-xl',
  'heading-h4-2xl',
  'heading-h4-3xl',
  'heading-h4-4xl',
  /* title sizes */
  'title-xl',
  'title-xl-xs',
  'title-xl-sm',
  'title-xl-md',
  'title-xl-lg',
  'title-xl-xl',
  'title-xl-2xl',
  'title-xl-3xl',
  'title-xl-4xl',
  'title-lg',
  'title-lg-xs',
  'title-lg-sm',
  'title-lg-md',
  'title-lg-lg',
  'title-lg-xl',
  'title-lg-2xl',
  'title-lg-3xl',
  'title-lg-4xl',

  /* body sizes */
  'body-xl',
  'body-xl-xs',
  'body-xl-sm',
  'body-xl-md',
  'body-xl-lg',
  'body-xl-xl',
  'body-xl-2xl',
  'body-xl-3xl',
  'body-xl-4xl',
  'body-lg',
  'body-lg-xs',
  'body-lg-sm',
  'body-lg-md',
  'body-lg-lg',
  'body-lg-xl',
  'body-lg-2xl',
  'body-lg-3xl',
  'body-lg-4xl',
  'body-md',
  'body-md-xs',
  'body-md-sm',
  'body-md-md',
  'body-md-lg',
  'body-md-xl',
  'body-md-2xl',
  'body-md-3xl',
  'body-md-4xl',

  /* caption sizes */
  'caption-sm',
  'caption-sm-xs',
  'caption-sm-sm',
  'caption-sm-md',
  'caption-sm-lg',
  'caption-sm-xl',
  'caption-sm-2xl',
  'caption-sm-3xl',
  'caption-sm-4xl',
  'caption-md',
  'caption-md-xs',
  'caption-md-sm',
  'caption-md-md',
  'caption-md-lg',
  'caption-md-xl',
  'caption-md-2xl',
  'caption-md-3xl',
  'caption-md-4xl'
]

const shadowSizes = [
  'shadow-flat',
  'shadow-overlay',
  'shadow-sticky-nav',
  'shadow-dialog'
]

const twMergeConfig = {
  extend: {
    theme: {
      color: colors,
      text: textSizes
    }
  }
}

defaultConfig.twMergeConfig = twMergeConfig

const customTwMerge = extendTailwindMerge({
  cacheSize: 2000,
  extend: {
    theme: {
      shadow: shadowSizes,
      color: colors,
      font: fontSizes,
      text: textSizes
    }
  }
})

export function cn(...inputs: ClassValue[]): string {
  return customTwMerge(clsx(inputs))
}
